import Vue from "vue";
import Vuex from "vuex";
import getNested from "lodash-es/get";
import sortBy from "lodash-es/sortBy";
import isEmpty from "lodash-es/isEmpty";
import groupBy from "lodash-es/groupBy";
import { titleCase } from "@/shared/string-utils";

Vue.use(Vuex);

export default {
  namespaced: true,

  state: () => {
    return {};
  },

  actions: {
    async getCommodities({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "get",
        apiUrl: "/entities/commodities",
        data: {}
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      let items = response.data.commodities.map(item => {
        return {
          value: item.commodityValue,
          text: titleCase(item.commodity),
          group: item.commodityGroup
        };
      });

      if (parameters.group) {
        // grouped for filter-panel type: "treeselect"
        items = groupBy(items, "group") || [];
        const commodityGroupNames = Object.keys(items).sort();

        return commodityGroupNames.map(groupName => {
          // allow to select commodity items
          let groupItems = items[groupName].map(item => ({
            id: item.value,
            label: item.text
          }));
          return { id: groupName, label: groupName, children: groupItems };
        });
      } else {
        // plain list
        return sortBy(items, ["text"]);
      }
    },

    async getSegments({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "get",
        apiUrl: "/entities/segments",
        data: {}
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      let items = response.data.segments.map(item => {
        return {
          value: item.subSegmentKey,
          text: item.subSegment,
          group: item.segment,
          order: item.cutoffLow || 0
        };
      });

      if (parameters.group) {
        // grouped for filter-panel type: "treeselect"

        items = groupBy(items, "group") || [];
        const segmentNames = Object.keys(items);

        return segmentNames.map(groupName => {
          // allow to select commodity items
          let groupItems = items[groupName].map(item => ({
            id: item.value,
            label: item.text
          }));
          return { id: groupName, label: groupName, children: groupItems };
        });
      } else {
        // plain list
        return sortBy(items, ["order"]);
      }
    },

    async getCountries({ dispatch }) {
      let response = await dispatch("apiRequest", {
        method: "get",
        apiUrl: "/entities/countries"
        // data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      let items = response.data.countries.map(item => {
        return {
          value: item.countryCode,
          text: titleCase(item.country)
        };
      });
      return sortBy(items, ["text"]);
    },

    async getTradeflowsRegionsWithPolygons({ dispatch }, parameters) {
      // expected parameters example:
      // {
      //   "direction": "export",
      //   "commodity": ["iron_ore"]
      // }

      let response = await dispatch("apiRequest", {
        method: "get",
        apiUrl: "/tradeflows/regionswithpolygon",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      let items = response.data.regions.map(item => {
        return {
          value: item.regionId,
          text: item.regionName,
          geoJSON: JSON.parse(item.geojson)
        };
      });
      return sortBy(items, ["text"]);
    },

    async getRegions({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "get",
        apiUrl: `/entities/regions`,
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      return response.data;
    },

    async getRegionsWithPolygons({ dispatch }) {
      let response = await dispatch("apiRequest", {
        method: "get",
        apiUrl: `/entities/regionswithpolygons`
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      let items = getNested(response, "data.regions", []).map(item => {
        return {
          value: item.regionId,
          text: item.regionName,
          geoJSON: JSON.parse(item.geojson)
        };
      });
      return sortBy(items, ["text"]);
    },

    async getVoyages({ dispatch }, parameters) {
      // laden legs "describes the loaded part of a voyage"
      // possible parameters example:
      // - commodity: iron_ore
      // - segment: capesize
      // - from_country_code: BR
      // - to_country_code: CN
      // - start/end dates?
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/tradeflows/data/voyages",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }
      return response;
    },

    async getModelVoyage({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/voyage/metrics",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }
      return response;
    },

    async getPortCalls({ dispatch }, parameters) {
      // laden legs "describes the loaded part of a voyage"
      // possible parameters example:
      // - commodity: iron_ore
      // - segment: capesize
      // - from_country_code: BR
      // - to_country_code: CN
      // - start/end dates?
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/portcalls",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }
      return response;
    },

    async getCountriesStat({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/tradeflows/data/country",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      return response.data;
    },

    async getCountriesExportStat({ dispatch }, parameters) {
      const response = await dispatch("getCountriesStat", {
        ...parameters,
        direction: "export"
      });
      return getNested(response, "countryVolumes", []);
    },

    async getCountriesImportStat({ dispatch }, parameters) {
      const response = await dispatch("getCountriesStat", {
        ...parameters,
        direction: "import"
      });
      return getNested(response, "countryVolumes", []);
    },

    async getPortsStat({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/tradeflows/data/port",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      response.data.coords = getNested(response, "data.coords", []).map(item =>
        isEmpty(item) ? null : [item.lon, item.lat]
      );

      return response.data;
    },

    async getPortsExportStat({ dispatch }, parameters) {
      const response = await dispatch("getPortsStat", {
        ...parameters,
        direction: "export"
      });
      return getNested(response, "portVolumes", []);
    },

    async getPortsImportStat({ dispatch }, parameters) {
      const response = await dispatch("getPortsStat", {
        ...parameters,
        direction: "import"
      });
      return getNested(response, "portVolumes", []);
    },

    async getVoyagesPeriodicStat({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: `/tradeflows/data/timeseries`,
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      if (getNested(response, "data.csv")) {
        // csv file link
        return response;
      } else {
        response.data = getNested(response, "data.data", []);
        return response;
      }
    },

    async getNetExportsStat({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: `/tradeflows/data/netexport`,
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      if (getNested(response, "data.csv")) {
        // csv file link
        return response;
      } else {
        response.data = getNested(response, "data.data", []);
        return response;
      }
    },

    async getVesselsStat({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/tradeflows/data/segment",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      return getNested(response, "data.segmentVolumes", []).map(item => ({
        segment: item.segment,
        volume: item.volume
      }));
    },

    async getMovementsStat({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/tradeflows/data/stats",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }
      return getNested(response, "data", {});
    },

    async getCommodityStat({ dispatch }, parameters) {
      let response = await dispatch("apiRequest", {
        method: "post",
        apiUrl: "/tradeflows/data/commodity",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return [];
      }

      return getNested(response, "data.commodityVolumes", []).map(item => ({
        segment: item.commodity,
        volume: item.volume
      }));
    },

    async portSearch({ dispatch }, parameters) {
      // available query parameters:
      // q=<text-to-search>
      // entity_type=<"port" or "berth"> (optional)
      // port_id=<id> (optional)
      let response = await dispatch("apiRequest", {
        method: "get",
        apiUrl: "/tradeflows/search",
        data: parameters
      });

      if (response.error) {
        console.log("api error", response);
        return null;
      }

      return getNested(response, "data.polygonEntities", []);
    },

    async apiRequest(store, parameters) {
      // here you can override common API request parameters for this api module
      // for example:
      // parameters: {...parameters, baseUrl: process.env.VUE_APP_BETA_API_URL},
      return store.dispatch(
        "api/apiRequest",
        { ...parameters, baseUrl: process.env.VUE_APP_BETA_API_URL_V2 },
        { root: true }
      );
    }
  }
};
